/**
 * หนึ่งแถว = หนึ่ง connected account
 *
 * ทุก action ส่ง `account.id` ของแถวตัวเอง ไม่ใช่ platform — UI เดิม resolve
 * account จาก Map ด้วย platformType (ในไฟล์ PlatformAccountsGrid ที่ถูกลบไปแล้ว) ซึ่งพอมี
 * 2 account ต่อ platform จะไปโดนตัวที่ค้างใน map ไม่ใช่ตัวที่กด
 *
 * ฝั่งขวาของแถวนำด้วย DATA frontier (ข้อมูลถึงวันไหน) แทน "synced X ago":
 * sync ที่จบเขียวโดยข้อมูลไม่ขยับมีจริง (Google ค้างที่ 08-24 ทั้งสัปดาห์)
 * เวลา sync จึงเป็นบรรทัดรอง ไม่ใช่คำตอบหลัก
 *
 * @module components/settings/PlatformAccountRow
 */

import React, { useRef, useState } from "react";
import { clsx } from "clsx";
import { CheckCircle2, Loader2, MoreHorizontal, Pencil, RefreshCw, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDismissable } from "@/hooks/useDismissable";
import { formatLastSync } from "./formatLastSync";
import { frontierFacts } from "./connectionSummary";
import { isHealthTestable, type AccountHealth } from "@/services/accountHealth";
import type { PlatformAccount, PlatformConfig, ConnectionMethod } from "@/types";

export interface PlatformAccountRowProps {
  account: PlatformAccount;
  /** identity of the row: icon + product name lead, account name is the sub-line */
  platform: Pick<PlatformConfig, "icon" | "displayName">;
  /** latest metric date this platform delivered; null = fetched, none found */
  dataThrough: string | null;
  /** true when the lookup failed — absence of a date then proves nothing. */
  frontierUnreadable?: boolean;
  /** "today" on the DATA calendar (Asia/Bangkok) — pass todayDataDate() */
  todayIso: string;
  isSyncing: boolean;
  /** live "Test connection" result for this account, if tested this session */
  health?: AccountHealth | null;
  /** true while a live test is in flight for this account */
  isTesting?: boolean;
  /**
   * Why the last FINISHED sync on this account wrote nothing — the worker's own
   * sentence out of `sync_jobs.error_message`, in English (see the disclosure
   * below). Null/absent whenever the last run delivered, failed, or has not
   * happened; `fetchEmptySyncReasons` decides which of those it is.
   */
  emptySyncReason?: string | null;
  onSync: (accountId: string) => void;
  onReconnect: (account: PlatformAccount) => void;
  onDisconnect: (accountId: string) => void;
  /** run a live credential check; omit to hide the affordance entirely */
  onTest?: (account: PlatformAccount) => void;
  /** edit the account's name / id; omit to hide the affordance */
  onEdit?: (account: PlatformAccount) => void;
}

// status ถูกคำนวณมาแล้วที่ toPlatformAccount (platformAccountService.ts:39-53)
// อย่าคำนวณซ้ำจาก expiresAt/tokenStatus ที่นี่ — สองที่จะไม่ตรงกันวันหนึ่ง
//
// จุดสีรายงานเฉพาะ "สุขภาพ credential" — frontier ที่หยุดนิ่งไม่เปลี่ยนจุดเป็น
// เหลือง เพราะบัญชีพัก (delivery หยุด) กับ token พัง เป็นคนละปัญหา คนละวิธีแก้
const STATUS_STYLE: Record<PlatformAccount["status"], string> = {
  active: "bg-green-500",
  expired: "bg-amber-500",
  error: "bg-amber-500",
  // neutral, not amber — "unverified" is "we don't know yet", not "broken"
  unverified: "bg-neutral-300",
  // red, not amber: amber says "needs attention soon". This one cannot sync at
  // all and never could — there is no credential stored against it.
  disconnected: "bg-red-500",
};

// How the account was connected — shown as a small badge so it's clear at a
// glance which credential path each connection uses.
const METHOD_BADGE: Record<ConnectionMethod, { labelKey: string; className: string }> = {
  token: { labelKey: "settings.methodToken2", className: "bg-neutral-100 text-neutral-700" },
  oauth2: { labelKey: "settings.methodOauth", className: "bg-blue-50 text-blue-700" },
  service_account: { labelKey: "settings.methodServiceAccount", className: "bg-indigo-50 text-indigo-700" },
};

// วันสั้นแบบไม่มีปี ("4 ก.ย.") — ตั้งใจไม่มีปีเพื่อเลี่ยงพ.ศ. 2569 โผล่ในป้ายสั้น
const formatShortDate = (isoDate: string, language: string): string =>
  new Intl.DateTimeFormat(language === "th" ? "th-TH" : "en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00Z`));

export const PlatformAccountRow: React.FC<PlatformAccountRowProps> = ({
  account,
  platform,
  dataThrough,
  frontierUnreadable = false,
  todayIso,
  isSyncing,
  health,
  isTesting = false,
  emptySyncReason = null,
  onSync,
  onReconnect,
  onDisconnect,
  onTest,
  onEdit,
}) => {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Every dismiss path (outside click, Escape, action chosen) closes the
  // popup — none of them should leave focus stranded on <body>.
  const closeMenu = (): void => {
    setMenuOpen(false);
    triggerRef.current?.focus();
  };

  useDismissable(menuRef, menuOpen, closeMenu);

  const isDemo = account.accountType === "demo_data";
  // Only offer the live test where a browser check is actually possible.
  const canTest = Boolean(onTest) && isHealthTestable(account);

  const runAction = (action: () => void): void => {
    closeMenu();
    action();
  };

  // ข้อความสถานะ: บอกสิ่งที่ต้องทำ ไม่ใช่แค่ย้อมสี ส่วนสาเหตุจริงของ error บอก
  // ไม่ได้ — sync_error ไม่ถูก map ขึ้นมาที่ client (platformAccountService.ts:55-61)
  //
  // demo แยกออกมาก่อนเพื่อน: มันไม่มี lastSyncAt (ของมันชื่อ lastSyncedAt และเป็น
  // Date — demoDataService.ts:1812) formatLastSync จึงคืน "Never" ทำให้แถวตัวอย่าง
  // ขึ้นว่า "synced Never" ซึ่งอ่านเหมือนของพัง ทั้งที่มันไม่เคยต้อง sync
  //
  // active ไม่มีบรรทัดสถานะ: เวลาที่ sync ล่าสุดย้ายไปคอลัมน์ขวา (ใต้ frontier)
  const statusText = isDemo
    ? t("settings.rowDemoStatus")
    : account.status === "disconnected"
      ? t("settings.rowDisconnected")
      : account.status === "expired"
        ? t("settings.rowExpired")
        : account.status === "error"
          ? t("settings.rowError")
          : account.status === "unverified"
            ? t("settings.rowUnverified")
            : null;

  /**
   * The row is green because somebody TESTED it, and nothing else here says so.
   *
   * `active` deliberately has no status line — the sync clock on the right
   * explains the green dot. An account that has never synced has no sync clock:
   * before last_verified_at existed it could not be green at all, and the only
   * visible result of a successful test would now be a nag that vanished, which
   * is not a result anyone can read. So the verification speaks in the slot the
   * nag just left, and only where it is the evidence doing the work.
   */
  const verifiedWithoutSync =
    !isDemo && account.status === "active" && !account.lastSyncAt && Boolean(account.lastVerifiedAt);

  const facts = frontierFacts(dataThrough, todayIso, frontierUnreadable);
  const frontierDate = dataThrough ? formatShortDate(dataThrough, i18n.language) : null;
  const frontierLabel =
    facts.staleDays === 0
      ? t("settings.dataThroughToday", { date: frontierDate })
      : facts.staleDays === 1
        ? t("settings.dataThroughYesterday", { date: frontierDate })
        : frontierDate;

  /**
   * A connection that has stopped, by either kind of evidence: a live test that
   * Google/Facebook/etc refused, or a stored status that already says so.
   *
   * Rendered as a banner rather than as one more small grey-red line, because
   * the complaint that prompted it was that a dropped Google Analytics looked
   * the same as a working one — its message was a truncated 12px string among
   * five other 12px strings, and the row's own dot was the only other tell.
   */
  const needsReconnect =
    !isDemo &&
    (health?.state === "failed" ||
      account.status === "expired" ||
      account.status === "disconnected" ||
      account.status === "error");

  /**
   * Whether this row owes the reader the reason its last sync found nothing.
   *
   * NOT part of `needsReconnect`, and that is the whole point. `no_data` was
   * invented so a dormant account's quiet window would stop burning red — see
   * the module comment on syncOutcome, and the same reasoning that keeps
   * `partial` out of the health panel. Telling somebody to reconnect a
   * credential the platform answered with minutes ago is wasted work, so this
   * gets no red border, no banner and no call to action: it is an explanation,
   * in neutral, and the dot and the frontier column keep saying what they said.
   *
   * Three suppressions, each for a reason the row has already learned:
   *
   *   - `needsReconnect` wins. An expired token is a different question with a
   *     different remedy, and the banner already owns this full-width slot.
   *     Two explanations stacked in one place is how the truncated-12px-string
   *     complaint started.
   *   - `isSyncing` holds it. A verdict on the previous run printed under a
   *     spinner reads as the verdict on the run in flight; usePlatformAccounts
   *     reloads the accounts when the job finishes, so it returns updated.
   *   - demo rows never sync, so they have nothing to explain.
   */
  const showEmptySyncReason =
    Boolean(emptySyncReason) && !isDemo && !needsReconnect && !isSyncing;

  return (
    <div className={clsx(needsReconnect && "border-l-2 border-error-500 bg-error-50/40")}>
    <div className="flex items-center gap-3 px-4 py-3">
      {isSyncing || isTesting ? (
        <Loader2
          role="status"
          aria-label={`${isTesting ? t('settings.rowTesting') : t('settings.rowSyncing')} ${account.accountName}`}
          className="w-3 h-3 shrink-0 text-blue-600 animate-spin"
        />
      ) : (
        <span
          className={clsx(
            "w-2 h-2 rounded-full shrink-0",
            // a live test result outranks the passive dot colour
            health?.state === "ready"
              ? "bg-green-500"
              : health?.state === "failed"
                ? "bg-red-500"
                : STATUS_STYLE[account.status],
          )}
        />
      )}

      <span className="text-lg shrink-0" aria-hidden="true">
        {platform.icon}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-ink-900 truncate">
            {platform.displayName}
          </span>
          {!isDemo && account.connectionMethod && METHOD_BADGE[account.connectionMethod] && (
            <span
              className={clsx(
                "px-2 py-0.5 rounded-full text-[11px] shrink-0",
                METHOD_BADGE[account.connectionMethod].className
              )}
              title={t("settings.connectedVia", { method: t(METHOD_BADGE[account.connectionMethod].labelKey) })}
            >
              {t(METHOD_BADGE[account.connectionMethod].labelKey)}
            </span>
          )}
          {isDemo && (
            <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-[11px] shrink-0">
              {t("settings.rowDemoData")}
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-700 truncate">
          {account.accountName}
          {/* type บอกว่า accountId เป็น string เสมอ แต่ demo accounts ไม่มีจริง
              (demoDataService.ts) และถูก cast ทับใน Settings.tsx —
              guard นี้จึงไม่ใช่ของฟุ่มเฟือย ไม่งั้นได้ "· undefined" */}
          {account.accountId && (
            <span className="text-neutral-700"> · {account.accountId}</span>
          )}
        </div>
        {isTesting ? (
          <div className="text-xs text-neutral-700">{t('settings.rowTestingConnection')}</div>
        ) : needsReconnect ? (
          // Suppressed on purpose: the banner below says this, in full and with
          // the action attached. Rendering both put the same sentence on the
          // row twice — once truncated, once not.
          null
        ) : health && !isDemo ? (
          <div
            className={clsx(
              "text-xs flex items-center gap-1",
              health.state === "ready"
                ? "text-green-700"
                : health.state === "failed"
                  ? "text-red-700"
                  : "text-neutral-700",
            )}
          >
            {health.state === "ready" ? (
              <CheckCircle2 size={12} className="shrink-0" aria-hidden="true" />
            ) : health.state === "failed" ? (
              <XCircle size={12} className="shrink-0" aria-hidden="true" />
            ) : null}
            <span className="truncate">{health.message}</span>
          </div>
        ) : statusText ? (
          <div
            className={clsx(
              "text-xs",
              isDemo || account.status === "unverified" ? "text-neutral-700" : "text-amber-700",
            )}
          >
            {statusText}
          </div>
        ) : verifiedWithoutSync ? (
          <div className="text-xs text-neutral-700">
            {t("settings.verifiedAgo", { ago: formatLastSync(account.lastVerifiedAt, t) })}
          </div>
        ) : null}
      </div>

      {/* DATA frontier — the answer the reader actually needs, in bold; the
          sync clock demoted to the line under it. Demo rows have no data. */}
      {!isDemo && (
        <div className="hidden sm:block text-right shrink-0 min-w-0">
          {facts.tone === "fresh" ? (
            <>
              <div className="text-xs text-ink-900">
                <span className="text-neutral-700">{t("settings.dataThrough")} </span>
                <span className="font-semibold">{frontierLabel}</span>
              </div>
              <div className="text-[11px] text-neutral-700">
                {t("settings.syncedAgo", { ago: formatLastSync(account.lastSyncAt, t) })}
              </div>
            </>
          ) : facts.tone === "dormant" ? (
            <>
              <div className="text-xs font-semibold text-amber-700">
                {t("settings.noDeliverySince", { date: frontierDate })}
              </div>
              {/* สรุปว่า token ปกติ เฉพาะเมื่อ status ยืนยันว่าปกติจริง —
                  บัญชีพักที่ token พังด้วย ต้องไม่ได้ป้ายปลอบใจ */}
              {account.status === "active" && (
                <div className="text-[11px] text-neutral-700">
                  {t("settings.dormantHealthyToken")}
                </div>
              )}
            </>
          ) : facts.tone === "unknown" ? (
            // NOT "no data": the query failed, so this says what we know —
            // nothing — rather than reporting a dormant account.
            <div className="text-xs text-neutral-700">{t("settings.frontierUnknown")}</div>
          ) : (
            <div className="text-xs text-neutral-700">{t("settings.noDataYet")}</div>
          )}
        </div>
      )}

      {isDemo ? null : (
        <>
          <button
            type="button"
            disabled={isSyncing}
            onClick={() => onSync(account.id)}
            aria-label={t("settings.rowSyncNowAria", { account: account.accountName })}
            className="shrink-0 inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RefreshCw size={12} aria-hidden="true" />
            {t("settings.syncRow")}
          </button>
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(account)}
              aria-label={t("settings.rowEditAria", { account: account.accountName })}
              className="shrink-0 inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
            >
              <Pencil size={12} aria-hidden="true" />
              {t("settings.editRow")}
            </button>
          )}
          <div className="relative shrink-0" ref={menuRef}>
            {/* No aria-haspopup: the popup below is plain buttons, not a
              role="menu" with menuitems and arrow-key navigation. Per the
              ARIA spec aria-haspopup="true" is announced identically to
              "menu", so there is no value that describes this popup
              honestly — aria-expanded alone is accurate. */}
            <button
              type="button"
              ref={triggerRef}
              aria-label={t("settings.rowActionsAria", { account: account.accountName })}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="p-1.5 rounded-md text-neutral-700 hover:bg-neutral-100"
            >
              <MoreHorizontal size={16} />
            </button>

            {menuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                {/* Sync/Edit ไม่อยู่ในเมนู: มีปุ่มบนแถวแล้ว หนึ่ง action หนึ่งที่ */}
                {canTest && (
                  <button
                    type="button"
                    disabled={isTesting}
                    onClick={() => runAction(() => onTest?.(account))}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-50 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    {t("settings.menuTestConnection")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => runAction(() => onReconnect(account))}
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-50"
                >
                  {t("settings.menuReconnect")}
                </button>
                <button
                  type="button"
                  onClick={() => runAction(() => onDisconnect(account.id))}
                  className="w-full px-3 py-1.5 text-left text-sm text-red-700 hover:bg-red-50"
                >
                  {t("settings.menuDisconnect")}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>

    {/* WHY A DISCLOSURE, and what is in the row versus behind it.
        The probe's sentences were written for a record, not for a 12px row —
        "the platform reports 0 lifetime spend here, so there is nothing to
        fetch … and no older window will hold anything either" is three lines
        wide, and five to ten rows of that is a wall. But the complaint that
        shaped this row was that a dropped Google Analytics "looked the same as
        a working one — its message was a truncated 12px string among five other
        12px strings", so clipping it would repeat the exact mistake. A control
        is neither: the row says a reason EXISTS and is one click from it, and
        the sentence then arrives whole, with its dates and its figure intact,
        which is the part that answers the question.

        Native <details>, so the row needs no state, no aria wiring and no
        focus management of its own, and keyboard and screen readers get the
        behaviour they already know.

        THE NOTE IS NOT TRANSLATED, deliberately. It is written by the worker,
        server-side, in English, and it names dates and money — the same
        register the `failed` path already renders raw from the same column.
        Translating it client-side would mean classifying the sentence by its
        prose, which is the fragility syncOutcome avoided by classifying at the
        catch: "re-matching a code out of prose". So a Thai reader is served by
        making the frame unambiguous instead — the control and the attribution
        line are Thai, and that line says whose words these are and that they
        are English, rather than leaving a wall of foreign text unexplained. */}
    {showEmptySyncReason && (
      <details className="px-4 pb-2.5 -mt-1 text-xs">
        <summary className="cursor-pointer text-neutral-700 hover:text-ink-900">
          {t("settings.emptySyncWhy")}
        </summary>
        <div className="mt-1.5 rounded-md bg-neutral-50 px-2.5 py-2">
          <div className="font-medium text-neutral-700">{t("settings.emptySyncNoteSource")}</div>
          {/* whitespace-normal and no truncate: the whole sentence or nothing. */}
          <p className="mt-0.5 whitespace-normal text-neutral-700">{emptySyncReason}</p>
        </div>
      </details>
    )}

    {/* The call to action, said once and in full. The reason is NOT truncated
        here: "Google refused the stored credential (invalid_grant)" is the
        difference between reconnecting and chasing a phantom, and it was being
        clipped mid-sentence. */}
    {needsReconnect && (
      <div
        role="status"
        className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 pb-2.5 -mt-1 text-xs text-error-800"
      >
        <span className="font-semibold">{t("settings.reconnectNeeded")}</span>
        <span className="text-error-700">
          {health?.state === "failed" ? health.message : (statusText ?? t("settings.connectionStopped"))}
        </span>
        <button
          type="button"
          onClick={() => runAction(() => onReconnect(account))}
          className="ml-auto rounded-md bg-error-600 px-2.5 py-1 font-medium text-white hover:bg-error-700"
        >
          {t("settings.reconnect")}
        </button>
      </div>
    )}
    </div>
  );
};

export default PlatformAccountRow;
